import { ILayer } from '../layer.type';
import { environment } from '../../../../../environments/environment';
import { _ } from '../../../utils';
import { getSharedLayer } from './shared';
import { TenantConfig } from './tenant-config.type';
import { SymbolLayout, SymbolPaint } from 'mapbox-gl';

const markerUrlBase = environment.assetRoot + '/data/T14/markers/';

const smallElementBaseZoom = 13;
const smallElementValveZoom = 12;
const smallElementsOtherZoom = 11;

const valveLayout: SymbolLayout = {
  'text-allow-overlap': true,
  'text-anchor': 'top',
  'text-radial-offset': 1,
  'text-size': [
    'step', ['zoom'],
    0,
    15, 10,
  ],
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    smallElementValveZoom, 0.28,
    16, 0.46,
  ],
};

const valvePaint: SymbolPaint = {
  'text-color': 'black',
  'text-halo-color': 'white',
  'text-halo-width': 2,
};

const othersLayout: SymbolLayout = {
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    smallElementValveZoom, 0.45,
    16, 0.8,
  ],
};

const customerLayout: SymbolLayout = {
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    smallElementBaseZoom, 0.1,
    16, 0.8,
  ],
};

const alarmPaint: SymbolPaint = {
  'icon-opacity': [
    'case',
    ['feature-state', 'has-alarms'], ['abs', ['sin', ['/', ['feature-state', 'timestamp'], 750]]],
    1,
  ],
};

/**
 * Neve
 */
export default (): TenantConfig => ({
  layerDefinitions: getLayers(),
  layerDefinitionDefaults: {
    metadata: {
      visibleByZoom: 6,
    },
  },
  hasMultipleMarkerModes: false,
  defaultCenter: {
    lat: 66.523,
    lng: 25.751,
  },
  defaultZoom: 10.82,
  images: [
    { id: 'asiakas', url: markerUrlBase + 'asiakas-Asiakas.png' },
    { id: 'asiakas-iso', url: markerUrlBase + 'asiakas-Asiakas_iso.png' },
    { id: 'lampolaitos-bio', url: markerUrlBase + 'running-Lämpölaitos_biokaasu.png' },
    { id: 'lampolaitos-kpa', url: markerUrlBase + 'running-Lämpölaitos_kpa.png' },
    { id: 'lampolaitos-oil', url: markerUrlBase + 'running-Lämpölaitos_öljy.png' },
    { id: 'lampolaitos', url: markerUrlBase + 'running-lämpölaitos.png' },
    { id: 'voimalaitos', url: markerUrlBase + 'running-Voimalaitos.png' },
  ],
  alarmFilters: [{
    id: 2,
    name: _('Priority'),
    type: 'dropdown-multiple',
    noFiltersShowsAll: true,
    filter_values: [
      {
        name: 'A-kriittiset',
        value: 1,
        boolean: true
      },
      {
        name: 'B-kriittiset',
        value: 2,
        boolean: true
      },
      {
        name: 'C-kriittiset',
        value: 3,
        boolean: true
      }
    ]
  }]
});

// vectorURL and vectorSourceLayer are filled in from encrypted-data/T14.json by MapService.addLayer
// numbering for used layers refers to: https://miproagile.atlassian.net/browse/MR-789
export function getLayers(): ILayer[] {
  return [
    // 1. Kaukolämpöverkosto (Julkinen osa)
    {
      id: 'Putki_julkinen',
      vectorSourceLayer: 'Putki_julkinen',
      name: 'Kaukolämpöverkosto',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'line',
        sourceType: 'vector',
        clickable: true,
        strokeColor: '#ee1786',
        strokeWeight: [
          'interpolate', ['linear'], ['zoom'],
          0, 0.5,
          10, 1.25,
          22, 4
        ],
        visible: false,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'Putki_pieni',
      vectorSourceLayer: 'Putki_pieni',
      name: 'Kaukolämpöverkosto (pienet putket)',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'line',
        sourceType: 'vector',
        clickable: true,
        strokeColor: '#ee1786',
        strokeWeight: 1,
        visible: false,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementBaseZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 2. Ei-julkinen verkosto (Kaukolämpöverkoston osa, esim. Laji: DN150/2Mpuk_ei_julkinen)
    {
      id: 'Putki_ei_julkinen',
      vectorSourceLayer: 'Putki_ei_julkinen',
      name: 'Kaukolämpöverkosto (ei-julkinen)',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'line',
        sourceType: 'vector',
        clickable: true,
        strokeColor: '#fd0150',
        strokeWeight: 1,
        visible: false,
      },
      mapboxOptions: {
        paint: {
          'line-gap-width': 2,
          'line-dasharray': [1, 1],
          'line-blur': 1
        },
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 3. Laitokset
    {
      id: 'Syottopiste',
      vectorSourceLayer: 'Syottopiste',
      name: 'Laitokset',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          urlExpression: [
            'case',
            ['==', ['get', 'Laji'], 'Voimalaitos'], 'voimalaitos',
            ['in', 'BIO', ['get', 'Polttoaine']], 'lampolaitos-bio',
            ['in', 'Turve', ['get', 'Polttoaine']], 'lampolaitos-kpa',
            ['in', 'Hake', ['get', 'Polttoaine']], 'lampolaitos-kpa',
            ['in', 'POK', ['get', 'Polttoaine']], 'lampolaitos-oil',
            ['in', 'POR', ['get', 'Polttoaine']], 'lampolaitos-oil',
            'lampolaitos',
          ],
        },
        visible: false,
      },
      mapboxOptions: {
        paint: alarmPaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'Aseman tai kaivon tunnus',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
          coordinates: false,
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // 4. Pumppaamot
    {
      id: 'Pumppuasema',
      vectorSourceLayer: 'Pumppuasema',
      name: 'Pumppaamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'running-Pumppuasema.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...othersLayout,
        },
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'Tunnus',
        hoverComponent: 'ElementDetailsHover',
        visibleByZoom: smallElementsOtherZoom,
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // 5. Kaivot
    // TODO
    // 6. Porasulkuventtiilit (Löytyy kasettiventtiilin alta)
    {
      id: 'Porasulkuventtiili',
      vectorSourceLayer: 'Porasulkuventtiili',
      name: 'Porasulkuventtiilit',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'PS',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 7. Kiertolenkit (Löytyy kasettiventtiilin alta)
    {
      id: 'Kiertolenkki',
      vectorSourceLayer: 'Kiertolenkki_sv',
      name: 'Kiertolenkit',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'running-kiertolenkki.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...othersLayout,
        },
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        visibleByZoom: smallElementsOtherZoom,
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 8. Elementtiventtiilit (alasvetovalikko)
    //    7 alaryhmää (Jäljelle jääneet osat kasettiventtiilin alta kun ottaa pois porasulkuventtiilit ja kiertolenkit).
    {
      id: 'Haaroitusventtiili_kaivossa',
      vectorSourceLayer: 'Haaroitusventtiili_kaivossa',
      name: 'Haaroitusventtiili kaivossa',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'HVK',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'Kaivosulkuventtiili',
      vectorSourceLayer: 'Kaivosulkuventtiili',
      name: 'Kaivosulkuventtiili',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'KS',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'Sulkuventtiili',
      vectorSourceLayer: 'Sulkuventtiili',
      name: 'Sulkuventtiili',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'S',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'SV_IV',
      vectorSourceLayer: 'SV_IV',
      name: 'SV+IV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'SV+IV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'SV_TV',
      vectorSourceLayer: 'SV_TV',
      name: 'SV+TV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'SV+TV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'IV_SV_IV',
      vectorSourceLayer: 'IV_SV_IV',
      name: 'IV+SV+IV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'IV+SV+IV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'TV_SV_TV',
      vectorSourceLayer: 'TV_SV_TV',
      name: 'TV+SV+TV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'TV+SV+TV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 9. Liittymät
    {
      id: 'Liittyma',
      vectorSourceLayer: 'Liittyma',
      name: 'Liittymät',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          urlExpression: [
            'case',
            ['>=', ['number', ['get', 'Lämmitettävä rakennustilavuus (m³)']], 100_000], 'asiakas-iso',
            'asiakas',
          ]
        },
        visible: false,
      },
      mapboxOptions: {
        layout: customerLayout,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementBaseZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 10. Verkostopisteet
    // TODO
    // 11. Paine-eromittauspisteet (Löytyy Solmupisteen alta, muut solmupisteet ei tarvitse näkyä)
    {
      id: 'Paine_eromittaus',
      vectorSourceLayer: 'Paine_eromittaus',
      name: 'Paine-eromittauspisteet',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'running-Paineeneromittauspiste.png',
        },
        visible: false,
      },
      mapboxOptions: {
        paint: alarmPaint,
        layout: {
          ...othersLayout,
        }
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'Tunnus',
        hoverComponent: 'ElementDetailsHover',
        visibleByZoom: smallElementsOtherZoom,
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // 12. Osoitenumerot
    getSharedLayer('houseNumbers'),
    // 13. Pinnanmuodot
    getSharedLayer('elevationContour'),
    getSharedLayer('depthContour'),
    // 14. Vesialueet
    getSharedLayer('groundWaterAreas'), // 14.1 Pohjavesialueet
    getSharedLayer('drainageAreas'), // 14.2 Valuma-alueet
    getSharedLayer('basinOutlets'), // 14.3 Purkupisteet
  ];
}
